import {
    Permission,
    registerAlert,
    registerDashboardWidget,
    setDashboardWidgetLayout,
} from '@vendure/admin-ui/core';
import { Router } from '@angular/router';
import { gql } from 'graphql-tag';
import { merge, interval } from 'rxjs';
import { map, distinctUntilChanged, skip } from 'rxjs/operators';

const GET_ONBOARDING_ALERT_DATA = gql`
    query GetOnboardingAlertData {
        onboardingState
        onboardingReviewSiteUrl
        onboardingProgress {
            catalogUpload
            storeApproval
            businessInfo
            fulfillmentSettings
            stripeConnect
        }
        getStoreSiteByChannel {
            id
            status
        }
    }
`;

function onboardingRecheck({ dataService }: any) {
    const channelChange$ = (dataService.client as any)
        .userStatus()
        .mapStream((d: any) => d?.userStatus?.activeChannelId ?? d?.activeChannelId ?? null)
        .pipe(
            distinctUntilChanged(),
            skip(1), // skip the initial emission, only react to actual changes
        );
    return merge(interval(60_000), channelChange$);
}

export default [
    registerAlert({
        id: 'onboarding-review-site-available',
        check: ({ dataService }) =>
            dataService.query(GET_ONBOARDING_ALERT_DATA).mapSingle((data: any) => ({
                onboardingState: data.onboardingState ?? null,
                reviewSiteUrl: data.onboardingReviewSiteUrl ?? null,
                storeSiteStatus: data.getStoreSiteByChannel?.status ?? null,
                storeApprovalDone: data.onboardingProgress?.storeApproval === 'completed',
                allProgressDone: false,
            })),
        recheck: onboardingRecheck,
        isAlert: (data) =>
            data.onboardingState !== 'completed' &&
            data.storeSiteStatus === 'review' &&
            !data.storeApprovalDone &&
            !!data.reviewSiteUrl,
        label: () => ({ text: 'Your store preview is ready — click to view' }),
        action: (data) => {
            window.open(data.reviewSiteUrl!, '_blank');
        },
    }),

    registerAlert({
        id: 'onboarding-store-approved-go-live',
        check: ({ dataService }) =>
            dataService.query(GET_ONBOARDING_ALERT_DATA).mapSingle((data: any) => {
                const progress = data.onboardingProgress ?? {};
                const allProgressDone =
                    progress.catalogUpload === 'completed' &&
                    progress.storeApproval === 'completed' &&
                    progress.businessInfo === 'completed' &&
                    progress.fulfillmentSettings === 'completed' &&
                    progress.stripeConnect === 'completed';
                return {
                    onboardingState: data.onboardingState ?? null,
                    reviewSiteUrl: data.onboardingReviewSiteUrl ?? null,
                    storeSiteStatus: data.getStoreSiteByChannel?.status ?? null,
                    allProgressDone,
                };
            }),
        recheck: onboardingRecheck,
        isAlert: (data) =>
            data.onboardingState !== 'completed' &&
            data.storeSiteStatus === 'review' &&
            data.allProgressDone,
        label: () => ({ text: 'Your store is all set — go to dashboard to launch' }),
        action: (_, { injector }) => {
            injector.get(Router).navigate(['/']);
        },
    }),

    registerDashboardWidget('sellerOnboarding', {
        title: 'Seller onboarding',
        supportedWidths: [12],
        loadComponent: () =>
            import('./widgets/onboarding-dashboard-widget/onboarding-dashboard-widget.component').then(m => m.default),
    }),
    registerDashboardWidget('newOrderSummary', {
        title: 'New Order Summary',
        supportedWidths: [4, 6, 8, 12],
        loadComponent: () => import('./widgets/new-order-summary-widget/new-order-summary-widget.component').then(m => m.default),
        requiresPermissions: [Permission.ReadOrder],
    }),
    registerDashboardWidget('metrics', {
        title: 'dashboard.metrics',
        supportedWidths: [4, 6, 8, 12],
        loadComponent: () => import('./widgets/order-chart-widget/order-chart-widget.component').then(m => m.default),
        requiresPermissions: [Permission.ReadOrder],
    }),
    registerDashboardWidget('latestOrders', {
        title: 'dashboard.latest-orders',
        supportedWidths: [4, 6, 8, 12],
        loadComponent: () => import('./widgets/latest-orders-widget/latest-orders-widget.component').then(m => m.default),
        requiresPermissions: [Permission.ReadOrder],
    }),
    setDashboardWidgetLayout([
        { id: 'sellerOnboarding', width: 12 },
        { id: 'metrics', width: 12 },
        { id: 'newOrderSummary', width: 12 },
        { id: 'reviews', width: 6 },
        { id: 'latestOrders', width: 6 },
    ]),
];
